import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TIMEZONES } from '../../constants/timezones';
import { format } from 'date-fns';
import debounce from 'lodash.debounce';
import { SubmitHandler, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { Switch } from '@headlessui/react';
import { ExclamationTriangleIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { api } from '../../lib/api';
import ParticipantCardItem from '../search/ParticipantCardItem';
import ParticipantsCombobox from '../search/ParticipantsCombobox';
import Button from '../shared/Button';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { IconButton } from '../shared/IconButton';
import SelectCalendarCategory from '../shared/SelectCalendarCategory';
import SelectSceneCardCombobox from '../shared/SelectSceneCardCombobox';
import { showToast } from '../shared/Toast';
import {
  CalendarCategory,
  CalendarEvent,
  CurrentUser,
  ProjectMember,
  ProjectRole,
  SceneCard,
} from './Calendar';
import EventFormField from './EventFormField';
import { filterProjectMembersBySearchTerm } from './eventFormUtils';
import { eventWithCategories, formatDates, formatWhen, convertEventTimeToUserTimezone, convertUserTimeToEventTimezone } from './eventUtils';
import { METADATA_MAP } from '../../constants/nylas';

export type EventFormInput = {
  title: string;
  description: string;
  location: string;
  participants?: string[];
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  calendarCategoryId: number;
  allDay: boolean;
  sceneCardId?: string;
  timezone?: string;
};

export interface Participant {
  id?: number;
  name?: string;
  email: string;
  role?: string;
  imageUrl?: string;
}

interface EventFormProps {
  projectId: string;
  selectedEvent: CalendarEvent;
  setSelectedEvent: React.Dispatch<React.SetStateAction<CalendarEvent>>;
  selectedDate: { start: Date; end: Date };
  setEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>;
  setRawEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>;
  calendarCategories: CalendarCategory[];
  currentUser: CurrentUser;
  setMode: (mode: 'view' | 'edit') => void;
  projectSceneCards: SceneCard[];
  projectMembers: ProjectMember[];
  projectMembersByEmail: Record<string, ProjectMember>;
  setIsEventInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  allDaySelected: boolean;
  setAllDaySelected: React.Dispatch<React.SetStateAction<boolean>>;
  projectRole: ProjectRole;
}

export function FormGroupTitle({ title }) {
  return <div className="text-gray-700 leading-6 font-medium">{title}</div>;
}

function safeFormat(date: Date | string, formatStr: string, timezone?: string): string {
  if (!date) return 'Invalid date';
  
  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    return 'Invalid date';
  }
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  try {
    if (timezone && timezone.trim() !== '') {
      return format(dateObj, formatStr, { timeZone: timezone });
    }
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

export function FormLabel({ label }) {
  return <label className="mb-2 block text-sm leading-5 font-medium text-gray-700">{label}</label>;
}

export default function Toggle({ value, onChange }) {
  return (
    <Switch
      checked={value}
      onChange={onChange}
      className={twMerge(
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        value ? 'bg-blue-500' : 'bg-gray-200'
      )}
    >
      <span className="sr-only">Use setting</span>
      <span
        aria-hidden="true"
        className={twMerge(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          value ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </Switch>
  );
}

// ==================== ADD THIS HELPER FUNCTION ====================
const parseDateString = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  
  // Parse YYYY-MM-DD format
  const [year, month, day] = dateStr.split('-').map(Number);
  
  // Create date in local timezone
  // Note: month is 0-indexed in JavaScript (0 = January)
  return new Date(year, month - 1, day);
};
// ==================== END HELPER FUNCTION ====================

// ==================== ADD ADJUSTMENT HELPER ====================
const adjustEventForCalendarDisplay = (event: CalendarEvent): CalendarEvent => {
  if (!event.allDay) return event;
  
  // For all-day events, add +1 day to end date for FullCalendar display
  const adjustedEnd = new Date(event.end);
  adjustedEnd.setDate(adjustedEnd.getDate() + 1);
  
  return {
    ...event,
    end: adjustedEnd,
  };
};
// ==================== END ADJUSTMENT HELPER ====================

export function EventForm({
  projectId,
  selectedEvent,
  setSelectedEvent,
  projectMembers,
  projectMembersByEmail,
  selectedDate,
  setEvents,
  setRawEvents,
  calendarCategories,
  currentUser,
  setMode,
  setIsEventInfoOpen,
  allDaySelected,
  projectSceneCards,
  setAllDaySelected,
  projectRole,
}: EventFormProps) {
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [searchResults, setSearchResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const hasFormChangedRef = useRef(false);
  const userTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const filteredCalendarCategories = useMemo(() => {
    if (projectRole === 'wardrobe' || projectRole === 'hair_and_makeup') {
      return calendarCategories.filter(
        (category) =>
          category.name === 'Hair & Makeup Fittings' || category.name === 'Costume Fittings'
      );
    }
    return calendarCategories;
  }, [calendarCategories, projectRole]);

  const { register, handleSubmit, setValue, reset, formState, watch, setFocus, control } =
    useForm<EventFormInput>({
      defaultValues: {
        calendarCategoryId: filteredCalendarCategories[0]?.id,
        allDay: allDaySelected,
        title: '',
        startTime: '',
        endTime: '',
        description: '',
        startDate: '',
        endDate: '',
        location: '',
        sceneCardId: '',
        timezone: userTimezone,
      },
      mode: 'onChange',
    });
  const { errors, dirtyFields } = formState;

  const isUpdating = useMemo(() => !!selectedEvent, [selectedEvent]);
  const isAllDay = watch('allDay');
  const currentTimezone = watch('timezone');

  useEffect(() => {
    setFocus('title', { shouldSelect: true });
  }, []);

  const resetForm = () => {
    reset({
      title: '',
      calendarCategoryId: filteredCalendarCategories[0].id,
      startTime: '',
      endTime: '',
      description: '',
      startDate: '',
      endDate: '',
      participants: [],
      location: '',
      allDay: false,
      sceneCardId: '',
      timezone: userTimezone,
    });
    setSelectedParticipants([]);
    setAllDaySelected(false);
    setMode('view');
  };

  useEffect(() => {
    if (!selectedEvent) return;

    const { title, description, location, participants, start, end, allDay, timezone, originalTimezone } = selectedEvent;
    
    const parseDate = (date: any): Date => {
      if (date instanceof Date) return date;
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? new Date() : parsed;
      }
      return new Date();
    };
    
    const startDateObj = parseDate(start);
    const endDateObj = parseDate(end);
    
    let editStart = startDateObj;
    let editEnd = endDateObj;
    
    if (!allDay && originalTimezone && originalTimezone.trim() !== '' && selectedEvent.displayTimezone) {
      try {
        const fromTz = selectedEvent.displayTimezone && selectedEvent.displayTimezone.trim() !== '' 
          ? selectedEvent.displayTimezone 
          : 'UTC';
        const toTz = originalTimezone && originalTimezone.trim() !== '' 
          ? originalTimezone 
          : 'UTC';
        
        editStart = convertUserTimeToEventTimezone(startDateObj, fromTz, toTz);
        editEnd = convertUserTimeToEventTimezone(endDateObj, fromTz, toTz);
        
      } catch (error) {
        console.error('Error converting times for editing:', error);
        editStart = startDateObj;
        editEnd = endDateObj;
      }
    }
    
    const { parsedStart, parsedEnd } = formatDates({ 
      start: editStart, 
      end: editEnd, 
      allDay 
    });

    const formatIfValid = (date: any, formatStr: string): string => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
      }
      try {
        return format(date, formatStr);
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    const startDate = parsedStart ? formatIfValid(parsedStart, 'yyyy-MM-dd') : '';
    const endDate = parsedEnd ? formatIfValid(parsedEnd, 'yyyy-MM-dd') : '';
    const startTime = parsedStart ? formatIfValid(parsedStart, 'HH:mm') : '';
    const endTime = parsedEnd ? formatIfValid(parsedEnd, 'HH:mm') : '';
    const sceneCardId = selectedEvent.sceneCardId || '';
    
    let eventTimezone = '';
    eventTimezone = (originalTimezone && originalTimezone.trim() !== '') ? originalTimezone : (timezone && timezone.trim() !== '' ? timezone : userTimezone);
    const formValues: EventFormInput = {
      title: title || '',
      description: description || '',
      location: location || '',
      startDate,
      endDate,
      startTime,
      endTime,
      calendarCategoryId: selectedEvent.calendarCategoryId || filteredCalendarCategories[0]?.id,
      sceneCardId: sceneCardId || '',
      timezone: eventTimezone,
      allDay: allDay || false,
    };
        
    reset(formValues, { 
      keepDefaultValues: false 
    });
    
    setAllDaySelected(allDay || false);

    const parsedParticipants: Participant[] = participants
      .map((email) => ({ email }))
      .filter(({ email }) => email !== currentUser.email);
    setSelectedParticipants(parsedParticipants);
  }, [selectedEvent, reset, currentUser.email, filteredCalendarCategories, userTimezone, setAllDaySelected]);

  const handleTimezoneChange = (newTimezone: string) => {
    if (isAllDay || !newTimezone) return;    
    const currentTimezone = watch('timezone');
    if (currentTimezone === newTimezone) return;
    
    try {
      const currentStartDate = watch('startDate');
      const currentEndDate = watch('endDate');
      const currentStartTime = watch('startTime');
      const currentEndTime = watch('endTime');
      
      if (!currentStartDate || !currentStartTime) return;
      
      const startInUtc = zonedTimeToUtc(
        new Date(`${currentStartDate}T${currentStartTime}`),
        userTimezone
      );
      const endInUtc = zonedTimeToUtc(
        new Date(`${currentEndDate}T${currentEndTime}`),
        userTimezone
      );
      
      const startInNewTz = utcToZonedTime(startInUtc, newTimezone);
      const endInNewTz = utcToZonedTime(endInUtc, newTimezone);
      
      setValue('startDate', format(startInNewTz, 'yyyy-MM-dd'));
      setValue('endDate', format(endInNewTz, 'yyyy-MM-dd'));
      setValue('startTime', format(startInNewTz, 'HH:mm'));
      setValue('endTime', format(endInNewTz, 'HH:mm'));
      setValue('timezone', newTimezone);
      
    } catch (error) {
      console.error('Error converting timezone:', error);
      setValue('timezone', newTimezone);
    }
  };

  useEffect(() => {
    if (!selectedDate?.start) return;
    
    setValue('startDate', format(selectedDate.start, 'yyyy-MM-dd'));
    setValue('endDate', format(selectedDate.end, 'yyyy-MM-dd'));
    setValue('startTime', format(selectedDate.start, 'HH:mm'));
    setValue('endTime', format(selectedDate.end, 'HH:mm'));
    setValue('allDay', allDaySelected);
    
    if (!allDaySelected && !selectedEvent) {
      setValue('timezone', userTimezone);
    }
  }, [selectedDate, allDaySelected, selectedEvent, userTimezone, setValue]);

  const handleSearchForParticipants = useCallback(
    debounce((searchTerm) => {
      const projectMembersWithoutCurrentUser = projectMembers.filter(
        ({ email }) => email !== currentUser.email
      );
      const filteredMembers = filterProjectMembersBySearchTerm(
        projectMembersWithoutCurrentUser,
        searchTerm
      );
      const uniqueFilteredMembers = filteredMembers.filter(
        ({ email }) => !selectedParticipants.some((participant) => participant.email === email)
      ); // remove already selected participants
      setSearchResults(uniqueFilteredMembers);
    }, 200),
    [projectMembers, selectedParticipants, currentUser.email]
  );

  const onSubmit: SubmitHandler<EventFormInput> = async (data) => {
    console.log('=== onSubmit START ===');
    console.log('Form data:', data);
    
    const {
      title,
      description,
      location,
      startTime,
      endTime,
      startDate,
      endDate,
      calendarCategoryId,
      allDay,
      sceneCardId,
      timezone,
    } = data;
    
    if (submitting) return;
    setSubmitting(true);
    
    try {
      // Add debug test to verify parseDateString is working
      console.log('=== DATE PARSING TEST ===');
      console.log('Using new Date("2025-12-18"):', new Date("2025-12-18").toString());
      console.log('Using parseDateString("2025-12-18"):', parseDateString("2025-12-18").toString());
      console.log('=== END TEST ===');
      
      const validateDate = (date: any): boolean => {
        if (!date) return false;
        const dateObj = date instanceof Date ? date : new Date(date);
        return !isNaN(dateObj.getTime());
      };

      if (!validateDate(startDate)) {
        showToast('Invalid start date.', 'danger');
        setSubmitting(false);
        return;
      }

      if (!validateDate(endDate)) {
        showToast('Invalid end date.', 'danger');
        setSubmitting(false);
        return;
      }

      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

      if (endDateObj < startDateObj) {
        showToast('End date cannot be before start date.', 'danger');
        setSubmitting(false);
        return;
      }
      
      const participants = selectedParticipants.map((participant) => ({ email: participant.email }));
      const isEditing = !!selectedEvent;
      
      console.log('Participants:', participants);
      console.log('Is editing:', isEditing);
      
      // FIX: Handle all-day event end date adjustment correctly
      let finalEndDate = endDate;
      
        // In onSubmit function:
      if (allDay) {
        console.log('All-day event detected');
        
        const startDateStr = startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate;
        const endDateStr = endDate instanceof Date ? format(endDate, 'yyyy-MM-dd') : endDate;
        
        console.log('Date strings for comparison:', { startDateStr, endDateStr });
        
        if (startDateStr !== endDateStr) {
          console.log('Multi-day all-day event detected - formatWhen will adjust end date');
        } else {
          console.log('Single-day all-day event');
        }
        
        // NO adjustment - formatWhen will handle it
        finalEndDate = endDateStr;
      } else {
        console.log('Timed event');
      }
      
      // FIX: Ensure times are strings for formatWhen
      const formatTimeForAPI = (time: any): string => {
        if (!time) return '00:00';
        
        if (typeof time === 'string') {
          return time;
        }
        
        if (time instanceof Date) {
          return format(time, 'HH:mm');
        }
        
        return '00:00';
      };
      
      const when = formatWhen({ 
        startDate: startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate, 
        endDate: finalEndDate, 
        startTime: allDay ? '00:00' : formatTimeForAPI(startTime), 
        endTime: allDay ? '00:00' : formatTimeForAPI(endTime), 
        allDay, 
        timezone: timezone 
      });
      
      console.log('Formatted when:', when);

      // Base event data
      const eventData: any = {
        when,
        title: title?.trim(),
        description: description?.trim(),
        location: location?.trim(),
        participants: participants,
        metadata: {
          [METADATA_MAP.calendar_category_id]: calendarCategoryId.toString(),
          [METADATA_MAP.organizer_email]: currentUser.email,
          [METADATA_MAP.scene_card_id]: sceneCardId || '',
          [METADATA_MAP.original_timezone]: timezone ? timezone : '',
        },
      };

      if (timezone) {
        eventData.timezone = timezone;
      }
      
      console.log('Event data to send:', eventData);

      async function updateEvent() {
        try {
          const organizerEmail = selectedEvent?.organizerEmail || currentUser.email;
          const participantsWithOrganizer = organizerEmail
            ? [...participants, { email: organizerEmail }]
            : participants;
          
          eventData.participants = participantsWithOrganizer;
          eventData.event_id = selectedEvent.id;

          console.log('Updating event with data:', eventData);
          
          const result = await api.put(`/projects/${projectId}/calendar/update_event`, {
            event_input: eventData
          });
          
          console.log('Update response:', result);
          
          const newEvent = result?.data;
          
          if (newEvent) {
            const newEventWithCategory = eventWithCategories([newEvent], calendarCategories)[0];
            
            if (!newEventWithCategory.originalTimezone && timezone) {
              newEventWithCategory.originalTimezone = timezone;
            }
            
            if (!newEventWithCategory.timezone && newEventWithCategory.originalTimezone) {
              newEventWithCategory.timezone = newEventWithCategory.originalTimezone;
            }
            
            const convertedEvent = convertEventTimeToUserTimezone(newEventWithCategory, userTimezone);
            
            // ==================== ADD THIS ADJUSTMENT ====================
            // Adjust all-day events for FullCalendar display
            const finalEvent = adjustEventForCalendarDisplay(convertedEvent);
            // ==================== END ADJUSTMENT ====================
            
            // Update rawEvents with the converted (non-adjusted) event
            // This is critical for eventsById lookup to return updated data
            setRawEvents((current) => {
              const eventsWithoutUpdatedEvent = current.filter(
                (event) => event.id !== selectedEvent.id
              );
              return [...eventsWithoutUpdatedEvent, convertedEvent];
            });

            // Update events with the FullCalendar-adjusted event
            setEvents((current) => {
              const eventsWithoutUpdatedEvent = current.filter(
                (event) => event.id !== selectedEvent.id
              );
              return [...eventsWithoutUpdatedEvent, finalEvent];
            });
            setSelectedEvent(convertedEvent);
            
            resetForm();
            showToast('Event updated', 'success');
            return;
          } else {
            throw new Error('No event data received from server');
          }
        } catch (error) {
          console.error('Update event error:', error);
          
          let errorMessage = 'It was not possible to update event';
          
          if (error?.response?.status === 422 && error?.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          showToast(errorMessage, 'danger');
          throw error;
        }
      }

      async function createEvent() {
        try {
          const participantsWithOrganizer = [...participants, { email: currentUser.email }];
          eventData.participants = participantsWithOrganizer;
          
          console.log('Creating event with data:', eventData);
          
          const result = await api.post(`/projects/${projectId}/calendar/create_event`, {
            event_input: eventData
          });
          
          console.log('Create response:', result);
          
          const newEvent = result?.data;
          
          if (newEvent) {
            console.log('=== PROCESSING CREATED EVENT ===');
            console.log('Raw event from server:', newEvent);
            
            const newEventWithCategory = eventWithCategories([newEvent], calendarCategories)[0];
            
            console.log('After eventWithCategories:', newEventWithCategory);
            
            const convertedEvent = convertEventTimeToUserTimezone(newEventWithCategory, userTimezone);
            
            // ==================== ADD THIS ADJUSTMENT ====================
            // Adjust all-day events for FullCalendar display
            const finalEvent = adjustEventForCalendarDisplay(convertedEvent);
            // ==================== END ADJUSTMENT ====================
            
            console.log('After convertEventTimeToUserTimezone:', convertedEvent);
            console.log('After adjustment for FullCalendar:', finalEvent);
            console.log('Event date check:', {
              originalStart: newEvent.start,
              processedStart: convertedEvent.start?.toString(),
              adjustedEnd: finalEvent.end?.toString(),
              dateNum: convertedEvent.start?.getDate?.(),
              monthNum: convertedEvent.start?.getMonth?.(),
              yearNum: convertedEvent.start?.getFullYear?.(),
            });
            
            // Update rawEvents with the converted (non-adjusted) event
            setRawEvents((current) => [...current, convertedEvent]);

            // Update events with the FullCalendar-adjusted event
            setEvents((current) => [...current, finalEvent]);
            setSelectedEvent(convertedEvent);
            
            resetForm();
            showToast('Event created', 'success');
            return;
          } else {
            throw new Error('No event data received from server');
          }
        } catch (error) {
          console.error('Create event error:', error);
          
          let errorMessage = 'It was not possible to create event';
          
          if (error?.response?.status === 422 && error?.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          showToast(errorMessage, 'danger');
          throw error;
        }
      }

      if (isUpdating) {
        await updateEvent();
      } else {
        await createEvent();
      }
      setMode('view');
    } catch (error) {
      console.log('Outer catch error:', error.message);
    } finally {
      setSubmitting(false);
      console.log('=== onSubmit END ===');
    }
  };

  const handleNewParticipant = (participant) => {
    if (!participant) return;
    if (selectedParticipants.some((selected) => selected.email === participant.email)) return;
    setSelectedParticipants((current) => [...current, { email: participant.email }]);
    setSearchResults((current) => current.filter((p) => p.email !== participant.email));
  };

  const handleRemoveParticipant = (participantToRemove) => {
    setSelectedParticipants((current) =>
      current.filter((participant) => participant.email !== participantToRemove.email)
    );
  };

  const handleDeleteEventClicked = () => {
    if (!selectedEvent.id) return;

    async function deleteEvent() {
      try {
        setShowDeleteConfirmationModal(false);
        const result = await api.delete(
          `/projects/${projectId}/calendar/delete_event/${selectedEvent.id}`
        );
        if (result.status !== 200) {
          throw new Error('Error deleting event');
        }
        showToast('Event deleted', 'success');
        setEvents((current) => current.filter((event) => event.id !== selectedEvent.id));
      } catch (error) {
        setShowDeleteConfirmationModal(false);
        showToast('Error deleting event', 'danger');
      }
    }
    deleteEvent();
    resetForm();
    setIsEventInfoOpen(false);
  };

  const handleDiscardChanges = () => {
    resetForm();
    setShowDiscardConfirmationModal(false);
    if (isUpdating) {
      setMode('view');
    } else {
      setIsEventInfoOpen(false);
    }
  };

  useEffect(() => {
    const dirtyValues = Object.keys(dirtyFields)?.length > 0;
    hasFormChangedRef.current = dirtyValues ? true : false;
  }, [formState]);

  const handleCloseFormButton = () => {
    const hasChanges = hasFormChangedRef?.current;
    if (hasChanges) {
      setShowDiscardConfirmationModal(true);
    } else {
      isUpdating ? setMode('view') : setIsEventInfoOpen(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        title="Delete event?"
        description="Are you sure you want to delete this event? This event and its details will be permanently deleted."
        onCancelLabel="Cancel"
        onCancelAction={() => setShowDeleteConfirmationModal(false)}
        onConfirmLabel="Confirm"
        onConfirmAction={handleDeleteEventClicked}
        isOpen={showDeleteConfirmationModal}
        onClose={() => setShowDeleteConfirmationModal(false)}
        icon={
          <div className="rounded-full p-3 bg-red-200">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500 " />
          </div>
        }
      />
      <ConfirmationDialog
        title="Discard changes?"
        description="If you discard changes, you'll delete any edits you made since you last saved."
        onCancelLabel="Continue editing"
        onCancelAction={() => setShowDiscardConfirmationModal(false)}
        onConfirmLabel="Discard"
        onConfirmAction={handleDiscardChanges}
        isOpen={showDiscardConfirmationModal}
        onClose={() => setShowDiscardConfirmationModal(false)}
        icon={
          <div className="rounded-full p-3 bg-red-200">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500 " />
          </div>
        }
      />
      <form className="flex flex-col relative w-full h-full overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
        <div className="lg:static w-full lg:w-72 bg-white flex flex-none gap-2 border-b p-4 justify-end items-center z-10 lg:z-0">
          {selectedEvent && (
            <TrashIcon
              onClick={() => setShowDeleteConfirmationModal(true)}
              className="text-red-500 w-5 h-5 cursor-pointer hover:text-red-600"
            />
          )}

          <IconButton onClick={handleCloseFormButton}>
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </IconButton>
        </div>
        <div className="flex-1 overflow-y-auto lg:pt-4 flex flex-col gap-4">
          <div className="border-b px-4 flex flex-col gap-4 pb-4">
            <FormGroupTitle title="What" />
            <div>
              <FormLabel label="Title" />
              <EventFormField
                type="text"
                name="title"
                placeholder={'Example title'}
                errors={errors.title}
                register={register}
                required={true}
                validations={{
                  maxLength: { message: 'Max number of characters (60) exceeded.', value: 60 },
                }}
              />
            </div>
            <div>
              <FormLabel label="Add to sub calendar" />
              <SelectCalendarCategory
                value={watch('calendarCategoryId')}
                onChange={(id) => setValue('calendarCategoryId', id)}
                calendarCategories={filteredCalendarCategories}
              />
            </div>
            <div>
              <FormLabel label="Attach a scene card" />
              <SelectSceneCardCombobox
                projectSceneCards={projectSceneCards}
                handleAttachSceneCard={(sceneCard) => setValue('sceneCardId', sceneCard.uuid)}
                removeSceneCard={() => setValue('sceneCardId', '')}
                currentSceneCardId={watch('sceneCardId')}
              />
            </div>
          </div>
          <div className="border-b px-4 flex flex-col gap-4 pb-4">
            <FormGroupTitle title="When" />
            <div className="flex items-center gap-2">
              <Toggle
                value={watch('allDay')}
                onChange={(value) => {
                  setValue('allDay', value);
                  setAllDaySelected(value);
                  if (!watch('timezone')) {
                    const savedTimezone = selectedEvent?.originalTimezone || selectedEvent?.timezone || userTimezone;
                    setValue('timezone', savedTimezone);
                  }
                }}
              />
              <span className="text-sm leading-5 font-medium text-gray-700">All day</span>
            </div>
            <div>
              <FormLabel label="Start Date" />
              <EventFormField
                type="date"
                name="startDate"
                placeholder={'01/01'}
                errors={errors.startDate}
                register={register}
                control={control}
                watch={watch}
                setValue={setValue}
                required={true}
              />
            </div>
            <div>
              <FormLabel label="End Date" />
              <EventFormField
                type="date"
                name="endDate"
                placeholder={'01/01'}
                errors={errors.endDate}
                register={register}
                control={control}
                watch={watch}
                setValue={setValue}
                required={true}
              />
            </div>
            {!isAllDay && (
              <>
                <div>
                  <FormLabel label="Start Time" />
                  <EventFormField
                    type="time"
                    name="startTime"
                    placeholder={'08:00'}
                    errors={errors.startTime}
                    register={register}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    required={true}
                  />
                </div>
                <div>
                  <FormLabel label="End Time" />
                  <EventFormField
                    type="time"
                    name="endTime"
                    placeholder={'10:00'}
                    errors={errors.endTime}
                    register={register}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    required={true}
                  />
                </div>
              </>
            )}
            <div>
              <FormLabel label="Timezone" />
              <select
                {...register('timezone', { 
                  required: !isAllDay 
                })}
                value={watch('timezone') || ''}
                onChange={(e) => {
                  handleTimezoneChange(e.target.value);
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-2 px-3 border"
              >
                <option value="">Select Timezone</option>
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              {!isAllDay && errors.timezone && (
                <p className="mt-1 text-sm text-red-600">Timezone is required</p>
              )}
            </div>
          </div>
          <div className="border-b px-4 flex flex-col gap-4 pb-4">
            <FormGroupTitle title="Where" />
            <div>
              <FormLabel label="Location" />
              <EventFormField
                type="text"
                name="location"
                placeholder={'Street 123'}
                errors={errors.location}
                register={register}
                required={false}
              />
            </div>
          </div>
          <div className="border-b px-4 flex flex-col gap-4 pb-4">
            <FormGroupTitle title="Who" />
            <ParticipantsCombobox
              participants={searchResults}
              handleNewParticipant={handleNewParticipant}
              onSearch={handleSearchForParticipants}
            />
            <div className="flex flex-col gap-1">
              {selectedParticipants.map((participant) => {
                const currentParticipant = projectMembersByEmail[participant.email] || participant;
                return (
                  <ParticipantCardItem
                    key={currentParticipant.id || participant.email}
                    name={currentParticipant.name}
                    role={currentParticipant.role}
                    imageUrl={currentParticipant.imageUrl}
                    email={currentParticipant.email}
                    onClose={() => handleRemoveParticipant(participant)}
                  />
                );
              })}
            </div>
          </div>
          <div className="px-4 flex flex-col gap-4 pb-24 lg:pb-8">
            <FormGroupTitle title="Details" />
            <div>
              <FormLabel label="Notes" />
              <EventFormField
                type="textarea"
                name="description"
                placeholder={'Event Description'}
                errors={errors.description}
                register={register}
                required={false}
              />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex flex-none items-center gap-2 h-20 w-full lg:w-72 z-10 lg:static lg:z-0 bg-white">
          <Button type="button" onClick={handleSubmit(onSubmit)}>
            <span>Save</span>
          </Button>
          <Button type="button" onClick={handleCloseFormButton} variant="secondary">
            <span>Cancel</span>
          </Button>
        </div>
      </form>
    </>
  );
}